import BigNumber from 'bignumber.js';
import debounce from 'lodash/debounce';
import { UseFormGetValues, UseFormReset, UseFormReturn, UseFormSetValue } from 'react-hook-form';
import Service from 'react-reservice';

import { EMainProductCodeType } from '@webtrader/core/api/config/company';
import { EMarginMode, ISymbolDetail } from '@webtrader/core/api/market/symbols/detail';
import { EOrderExpiry, EOrderIndicate, EOrderType, IOrder } from '@webtrader/core/api/order';
import { IPendingOrder } from '@webtrader/core/api/order/pending';
import { ETradeFillPolicy } from '@webtrader/core/api/trade';
import limitTradeAPI, {
  ICreateLimitTradeParams,
  IUpdateLimitTradeParams,
  TLimitTradeOrderType
} from '@webtrader/core/api/trade/limit';
import marketTradeAPI, {
  ICreateMarketTradeParams,
  IUpdateMarketTradeParams,
  TMarketTradeOrderType
} from '@webtrader/core/api/trade/market';
import { EUserPreferenceCategory, EUserPreferenceType } from '@webtrader/core/api/user/preference';
import { calculateCopyToOrder } from '@webtrader/core/calculation/order';
import { isPROD, isUAT } from '@webtrader/core/configs';
import { EBuySellType, EOrderTypeLabel } from '@webtrader/core/constants/order';
import { getSecondsSinceEpoch } from '@webtrader/core/dayjs';
import {
  formatDecimal,
  formatNumberWithThousandSeparator,
  getDecimalsFromValue,
  numberAsBigNumberOrUndefined,
  numberOrDefault
} from '@webtrader/core/format/number';
import {
  checkIsOpenPosition,
  convertVolumeToUnits,
  mapOrderTypeToText,
  trimTrailingZero
} from '@webtrader/core/format/order';
import i18n from '@webtrader/core/i18n';
import { IApiError } from '@webtrader/core/request/api';
import { mixpanelAnalysisService } from '@webtrader/core/services/analysis';
import { EMixpanelPositionModuleTab, EOrderFieldAction } from '@webtrader/core/services/analysis/constants';
import {
  IMixpanelPlaceOrderProperties,
  IMixpanelPropertiesEditOrder,
  IPlaceOrderDetailsButtonClickProperties
} from '@webtrader/core/services/analysis/helpers/PlaceOrderHelper/type';
import { authService } from '@webtrader/core/services/auth';
import { chartService } from '@webtrader/core/services/chart';
import { companyService } from '@webtrader/core/services/company';
import { configService } from '@webtrader/core/services/config';
import { EAppType } from '@webtrader/core/services/config/service';
import { orderService } from '@webtrader/core/services/order';
import { destructureAsBigNumber, destructureAsNumber, destructureObject } from '@webtrader/core/utils';
import { getTradeCapabilities, isBuyTypeOrder, isMarketOrder } from '@webtrader/core/utils/order';

import { userPreferenceService } from '../../userPreference';

export const DEFAULT_MARGIN_REQUIREMENT = '0.00';
export const DEFAULT_MARGIN_USED = '0.00';
export const DEFAULT_TRADE_SIZE = '0.00';
export const DEFAULT_AMOUNT_PER_TICK = '0.00';
export const DEFAULT_AMOUNT_PER_TICK_PREFIX = '+/- ';

export interface IOrderFormValues {
  indicate: EOrderIndicate;
  tradeExpiry: EOrderExpiry;
  price?: number;
  lotSize?: number;
  stopLoss?: number;
  takeProfit?: number;
}

export interface IOrderFormData {
  volume?: string;
  units?: string;
  fillPolicy?: ETradeFillPolicy;
  price?: string;
  priceTrigger?: string;
  indicate?: EOrderIndicate;
  stopLoss?: string;
  takeProfit?: string;
  tradeExpiry?: EOrderExpiry;
  expiration?: number;
  deviation?: number;
  orderTypeLabel?: EOrderTypeLabel;
  centroidDeviation?: string;
}

export enum EPricePointsChangeType {
  VALUE,
  INCREMENT,
  DECREMENT
}

export interface IGenerateOrderPlacedMessageParams {
  indicate?: EOrderIndicate;
  price?: number;
  lotSize?: number;
  stopLoss?: number;
  takeProfit?: number;
  orderId?: number;
  priceTrigger?: number;
  units?: string;
}

interface ICopyToOrderParams {
  values: {
    price: number;
    stopLoss: number;
    takeProfit: number;
  };
  orderType: EOrderType;
}

interface IResetFormDataParams {
  integration?: EMainProductCodeType;
  override?: Partial<IOrderFormData>;
  retainVolume?: boolean;
  callback?: () => void;
}

interface IOrderFormCustomMethods {
  setSLTP?: (stopLoss: string | undefined, takeProfit: string | undefined) => void;
  resetSLTP?: () => void;
  recalculateSLTP?: () => void;
}

interface IGetPricePointsDescParams {
  volume?: string;
  price?: string;
  excludeCurrency?: boolean;
}

export interface IOrderFormContext {
  loading: boolean;
  loadingSymbolDetails: boolean;
  order?: IOrder;
  pendingOrder?: IPendingOrder;
  orderType?: EOrderType;
  buySellType?: EBuySellType;
  orderTypeLabel?: EOrderTypeLabel;
  symbolDetails?: ISymbolDetail;
  savedVolume?: string;
  savedUnits?: string;
  resetFormDataParams?: IResetFormDataParams;
  showOrderFormSheet?: boolean;
}

export interface ICreateTradeFunctionParams {
  values: IOrderFormData;
  mixpanelProperties: IMixpanelPlaceOrderProperties;
}

export interface ISetBuySellTypeParams {
  buySellType: EBuySellType;
  mixpanelProperties?: IPlaceOrderDetailsButtonClickProperties;
}

export interface IUpdateOrderParams {
  values: IOrderFormData;
  mixpanelProperties: IMixpanelPropertiesEditOrder;
}

export const enum EVolumeMode {
  SIZE,
  UNITS
}

export const enum EVolumeModeText {
  SIZE = 'SIZE',
  UNITS = 'UNITS'
}

interface ICalculateMarginUsedParams {
  contractSize: number;
  entryPrice: number;
  exchangeRate: number;
  marginMode: EMarginMode;
  marginPercentage: number;
  volume?: number;
}

interface ICalculateStopLosInPointsParams {
  currentPrice: number;
  orderType: EOrderType;
  pointStep: number;
  stopLoss: number;
}

interface ICalculatePotentialGainOrLoss {
  buySellType: EBuySellType;
  contractSize: number;
  currentPrice: BigNumber;
  exchangeRate: number;
  refValue: number;
  volume: number;
}

const defaultContext: IOrderFormContext = {
  loading: false,
  loadingSymbolDetails: false,
  order: undefined, // for edit market orders
  pendingOrder: undefined, // for edit pending orders
  orderType: undefined,
  buySellType: undefined,
  orderTypeLabel: undefined,
  symbolDetails: undefined,
  savedVolume: undefined,
  savedUnits: undefined,
  resetFormDataParams: undefined,
  showOrderFormSheet: false
};

abstract class OrderFormService extends Service<IOrderFormContext> {
  protected isEdit = false;

  protected estimatedStopLoss?: number;
  protected estimatedTakeProfit?: number;

  public formSetValue?: UseFormSetValue<IOrderFormData>;
  public formGetValues?: UseFormGetValues<IOrderFormData>;
  public formReset?: UseFormReset<IOrderFormData>;
  public formSetSLTP?: (stopLoss: string | undefined, takeProfit: string | undefined) => void;
  public formResetSLTP?: () => void;
  public formRecalculateSLTP?: () => void;

  constructor() {
    super();

    this.context = {
      ...defaultContext
    };
  }

  public init = (symbolDetails: ISymbolDetail) => {
    this.setSymbolDetails(symbolDetails);
    const { tradeMode } = symbolDetails;
    const appType = configService.getAppType();
    const { isShortAllowed } = getTradeCapabilities(tradeMode);

    if (appType === EAppType.PC) {
      const isMTSGoldX =
        (isUAT() && companyService.context.company?.tenantId === 'demo2') ||
        (isPROD() && companyService.context.company?.tenantId === 'mtsgoldx');
      this.setOrderType(isMTSGoldX && isShortAllowed ? EOrderType.SELL : EOrderType.BUY);
    }
  };

  protected getBuySellTypeFromOrderType = (orderType?: EOrderType) => {
    switch (orderType) {
      case EOrderType.BUY:
      case EOrderType.BUY_LIMIT:
      case EOrderType.BUY_STOP:
      case EOrderType.BUY_STOP_LIMIT:
        return EBuySellType.BUY;
      case EOrderType.SELL:
      case EOrderType.SELL_LIMIT:
      case EOrderType.SELL_STOP:
      case EOrderType.SELL_STOP_LIMIT:
        return EBuySellType.SELL;
      default:
        return EBuySellType.SELL;
    }
  };

  public copyToOrder = (params: ICopyToOrderParams) => {
    const { symbolDetails } = this.context;
    const { values, orderType } = params;
    const { stopLoss, takeProfit, price } = values;

    const { ask: liveAsk, bid: liveBid, decimal } = symbolDetails || {};

    switch (orderType) {
      case EOrderType.BUY:
      case EOrderType.SELL:
        if (liveAsk && liveBid) {
          this.setOrderType(orderType);
          this.setShowOrderFormSheet(true);
          const { stopLoss: newStopLoss, takeProfit: newTakeProfit } = calculateCopyToOrder({
            buy: liveAsk,
            sell: liveBid,
            orderType,
            copiedStopLoss: stopLoss,
            copiedTakeProfit: takeProfit
          });
          const stopLossBN = numberAsBigNumberOrUndefined(newStopLoss);
          const takeProfitBN = numberAsBigNumberOrUndefined(newTakeProfit);
          this.doResetFormData({
            override: {
              indicate: EOrderIndicate.PRICE
            },
            callback: () => {
              this.formSetSLTP?.(stopLossBN?.toFixed(decimal || 0), takeProfitBN?.toFixed(decimal || 0));
            }
          });
        }
        break;

      case EOrderType.BUY_STOP:
      case EOrderType.BUY_LIMIT:
      case EOrderType.SELL_STOP:
      case EOrderType.SELL_LIMIT:
        this.setOrderType(orderType);
        this.setShowOrderFormSheet(true);
        const stopLossBN = numberAsBigNumberOrUndefined(stopLoss);
        const takeProfitBN = numberAsBigNumberOrUndefined(takeProfit);
        const priceBN = numberAsBigNumberOrUndefined(price);
        this.doResetFormData({
          override: {
            price: priceBN?.toFixed(decimal || 0),
            indicate: EOrderIndicate.PRICE
          },
          callback: () => {
            this.formSetSLTP?.(stopLossBN?.toFixed(decimal || 0), takeProfitBN?.toFixed(decimal || 0));
          }
        });
        break;

      default:
        break;
    }
  };

  public setSymbolDetails = (symbolDetails: ISymbolDetail) => {
    this.context.symbolDetails = symbolDetails;
  };

  public setLiveBuySell = (buy: number, sell: number) => {
    const { symbolDetails } = this.context;

    if (symbolDetails) {
      this.context.symbolDetails = {
        ...symbolDetails,
        ask: buy,
        bid: sell
      };
    }
  };

  public setIsEdit = (editing: boolean) => {
    this.isEdit = editing;
  };

  public reset = () => {
    this.context = {
      ...defaultContext
    };

    this.estimatedStopLoss = undefined;
    this.estimatedTakeProfit = undefined;
    this.context.savedUnits = undefined;
    this.context.savedVolume = undefined;
  };

  public setFormControls = (formControls?: Partial<UseFormReturn<IOrderFormData>>) => {
    if (formControls) {
      this.formSetValue = formControls.setValue;
      this.formGetValues = formControls.getValues;
      this.formReset = formControls.reset;
      // To set saved volume value into new form when switching form methods (i.e. switching form)
      formControls.reset?.({ volume: this.context.savedVolume, units: this.context.savedUnits });
    } else {
      this.formSetValue = undefined;
      this.formGetValues = undefined;
      this.formReset = undefined;
    }
  };

  public setFormCustomMethods = (otherMethods?: IOrderFormCustomMethods) => {
    this.formSetSLTP = otherMethods?.setSLTP;
    this.formResetSLTP = otherMethods?.resetSLTP;
    this.formRecalculateSLTP = otherMethods?.recalculateSLTP;
  };

  public clearFormControls = () => {
    this.formSetValue = undefined;
    this.formGetValues = undefined;
    this.formReset = undefined;
  };

  public clearFormCustomMethods = () => {
    this.formSetSLTP = undefined;
    this.formResetSLTP = undefined;
    this.formRecalculateSLTP = undefined;
  };

  private mapVolumeModeToText = (volumeMode: EVolumeMode) => {
    const volumeModeMap = {
      [EVolumeMode.SIZE]: EVolumeModeText.SIZE,
      [EVolumeMode.UNITS]: EVolumeModeText.UNITS
    };
    return volumeModeMap[volumeMode];
  };

  public handleVolumeModeChange = (volumeMode: EVolumeMode) => {
    userPreferenceService.updateVolumeModePreference({
      value: volumeMode,
      params: {
        category: EUserPreferenceCategory.SWAP_UNITS,
        type: EUserPreferenceType.SWAP_UNITS,
        value: this.mapVolumeModeToText(volumeMode),
        showLoading: false // To prevent OCT toggle auto reload
      }
    });
  };

  public getVolumeDesc = (volume?: string | number) => {
    const { contractSize, marginCurrency } = this.context.symbolDetails || {};
    const convertedVolume = volume ?? '';
    const convertedContractSize = contractSize ?? 0;
    const calculatedVal = new BigNumber(convertedVolume).times(convertedContractSize);
    return calculatedVal.isNaN() ? undefined : `${calculatedVal.toFormat(2)} ${marginCurrency}`;
  };

  public handleVolumeChange = (value?: string, isSize = true) => {
    const { contractSize } = this.context.symbolDetails || {};
    if (!value || !contractSize) {
      this.setVolumeAndUnits(value, value);
      return;
    }

    const numericValue = new BigNumber(value);
    if (isSize) {
      const units = numericValue.times(contractSize);
      this.setVolumeAndUnits(value, units.toString());
    } else {
      const size = numericValue.div(contractSize);
      this.setVolumeAndUnits(size.toString(), value);
    }
  };

  private setVolumeAndUnits = (volume?: string, units?: string) => {
    this.formSetValue?.('volume', volume, { shouldValidate: true, shouldDirty: true });
    this.formSetValue?.('units', units, { shouldValidate: true, shouldDirty: true });
    this.context.savedVolume = volume;
    this.context.savedUnits = units;
  };

  public handleVolumeValidate = (volumeBN?: BigNumber): string | undefined => {
    if (!volumeBN) {
      return undefined;
    }
    const { symbolDetails } = this.context;
    if (!symbolDetails) {
      this.formSetValue?.('volume', undefined, { shouldValidate: true, shouldDirty: true });
      return undefined;
    }
    const { lotSizeStep, minContractLotSize, maxContractLotSize } = destructureAsBigNumber(symbolDetails, [
      'lotSizeStep',
      'minContractLotSize',
      'maxContractLotSize'
    ]);
    let newVolumeBN: BigNumber | undefined;
    if (volumeBN.lt(minContractLotSize)) {
      newVolumeBN = minContractLotSize;
    } else if (volumeBN.gt(maxContractLotSize)) {
      newVolumeBN = maxContractLotSize;
    } else {
      // Round down the volume to the nearest multiple of lotSizeStep
      // When lotSizeStep is 0.01 , the volume should always be a multiple of 0.01
      newVolumeBN = volumeBN.dividedToIntegerBy(lotSizeStep).times(lotSizeStep);
    }
    const finalVolume = trimTrailingZero(newVolumeBN.toFixed(lotSizeStep.decimalPlaces() ?? 0));
    this.formSetValue?.('volume', finalVolume, { shouldValidate: true, shouldDirty: true });
    return finalVolume;
  };

  private handleUnitsValidate = (unitsBN?: BigNumber): string | undefined => {
    if (!unitsBN) {
      return undefined;
    }
    const { symbolDetails } = this.context;
    if (!symbolDetails) {
      this.formSetValue?.('units', undefined, { shouldValidate: true, shouldDirty: true });
      return undefined;
    }
    const { contractSize, minContractLotSize, maxContractLotSize, lotSizeStep } = symbolDetails;

    const minUnits = new BigNumber(minContractLotSize).times(contractSize);
    const maxUnits = new BigNumber(maxContractLotSize).times(contractSize);
    const unitLotSizeStep = new BigNumber(lotSizeStep).times(contractSize);

    let newUnitsBN: BigNumber | undefined;
    if (unitsBN.lt(minUnits)) {
      newUnitsBN = minUnits;
    } else if (unitsBN.gt(maxUnits)) {
      newUnitsBN = maxUnits;
    } else {
      // Round down the units to the nearest multiple of minUnits
      // When lotSizeStep is 50 , the units should always be a multiple of 50
      newUnitsBN = unitsBN.dividedToIntegerBy(unitLotSizeStep).times(unitLotSizeStep);
    }

    const finalUnits = trimTrailingZero(newUnitsBN?.toFixed(unitLotSizeStep.decimalPlaces() ?? 0));
    this.formSetValue?.('units', finalUnits, { shouldValidate: true, shouldDirty: true });
    return finalUnits;
  };

  public handleVolumeBlur = () => {
    const volume = this.formGetValues?.('volume');
    const units = this.formGetValues?.('units');
    let finalVolume: string | undefined;
    let finalUnits: string | undefined;

    if (volume) {
      finalVolume = this.handleVolumeValidate(new BigNumber(volume));
    } else {
      finalVolume = undefined;
    }

    if (units) {
      finalUnits = this.handleUnitsValidate(new BigNumber(units));
    } else {
      finalUnits = undefined;
    }

    this.context.savedVolume = finalVolume;
    this.context.savedUnits = finalUnits;
  };

  private handleVolumeAndUnitsAdjust = (isIncrement?: boolean) => {
    const { lotSizeStep, contractSize, minContractLotSize } = this.context.symbolDetails || {};
    if (!lotSizeStep || !contractSize || minContractLotSize === undefined) {
      return;
    }
    const lotSizeStepBN = new BigNumber(numberOrDefault(this.context.symbolDetails?.lotSizeStep));
    const minContractLotSizeBN = new BigNumber(minContractLotSize);

    const volume = this.formGetValues?.('volume');
    const units = this.formGetValues?.('units');

    let finalVolume: string | undefined;
    let finalUnits: string | undefined;

    if (volume && units) {
      const nextValueBN = isIncrement
        ? new BigNumber(volume).plus(lotSizeStepBN)
        : new BigNumber(volume).minus(lotSizeStepBN);
      finalVolume = this.handleVolumeValidate(nextValueBN);
      finalUnits = this.handleUnitsValidate(nextValueBN.times(contractSize));
    } else {
      const nextValueBN = lotSizeStepBN.lte(minContractLotSizeBN) ? minContractLotSizeBN : lotSizeStepBN;
      finalVolume = this.handleVolumeValidate(nextValueBN);
      finalUnits = this.handleUnitsValidate(nextValueBN.times(contractSize));
      this.formSetValue?.('volume', finalVolume, { shouldValidate: true, shouldDirty: true });
      this.formSetValue?.('units', finalUnits, { shouldValidate: true, shouldDirty: true });
    }
    this.context.savedVolume = finalVolume;
    this.context.savedUnits = finalUnits;
  };

  public handleVolumeIncrease = () => this.handleVolumeAndUnitsAdjust(true);
  public handleVolumeDecrease = () => this.handleVolumeAndUnitsAdjust(false);

  /** Centroid Deviation */
  public handleCentroidDeviationChange = (value?: string) => {
    // Only allow whole numbers, filter out decimals and non-numeric chars
    const cleanValue = value?.replace(/[^\d]/g, '');
    this.formSetValue?.('centroidDeviation', cleanValue, { shouldValidate: true, shouldDirty: true });
  };

  public handleCentroidDeviationIncrease = () => {
    const currentDeviation = this.formGetValues?.('centroidDeviation');

    // If no value, set to 0 and increment by 1
    const nextValue =
      currentDeviation && numberOrDefault(currentDeviation) === 0
        ? new BigNumber(currentDeviation).plus(1)
        : new BigNumber(0);

    this.formSetValue?.('centroidDeviation', nextValue.toString(), { shouldValidate: true, shouldDirty: true });
  };

  public handleCentroidDeviationDecrease = () => {
    const currentDeviation = this.formGetValues?.('centroidDeviation');
    if (!currentDeviation) {
      return;
    }
    const nextValue = new BigNumber(currentDeviation).minus(1);
    if (nextValue.isLessThan(0)) {
      return;
    }
    this.formSetValue?.('centroidDeviation', nextValue.toString(), { shouldValidate: true, shouldDirty: true });
  };

  public handlePriceChange = (value?: string) => {
    this.formSetValue?.('price', value, { shouldValidate: true, shouldDirty: true });
  };

  public handlePriceBlur = () => {
    const { decimal = 0 } = this.context.symbolDetails || {};
    const price = this.formGetValues?.('price');
    let priceBN = price ? new BigNumber(price) : undefined;
    if (priceBN?.isNegative()) {
      priceBN = undefined;
    }
    this.formSetValue?.('price', priceBN?.toFixed(decimal), { shouldValidate: true, shouldDirty: true });
    if (priceBN) {
      this.formRecalculateSLTP?.();
    }
  };

  // eslint-disable-next-line complexity
  public handlePriceAdjust = (isIncrement?: boolean) => {
    const { orderType, symbolDetails } = this.context;

    if (!symbolDetails) {
      this.formSetValue?.('price', undefined, { shouldValidate: true, shouldDirty: true });
      this.formRecalculateSLTP?.();
      return;
    }

    const { ask, bid, pointStep, stopsLevel, decimal } = destructureAsNumber(symbolDetails, [
      'ask',
      'bid',
      'pointStep',
      'stopsLevel',
      'decimal'
    ]);
    let refPriceBN = undefined;
    let isMinimum = false;
    switch (orderType) {
      case EOrderType.BUY_LIMIT:
        isMinimum = false;
        refPriceBN = new BigNumber(ask).minus(new BigNumber(stopsLevel).times(pointStep));
        break;
      case EOrderType.SELL_LIMIT:
        isMinimum = true;
        refPriceBN = new BigNumber(stopsLevel).times(pointStep).plus(bid);
        break;
      case EOrderType.BUY_STOP:
      case EOrderType.BUY_STOP_LIMIT:
        isMinimum = true;
        refPriceBN = new BigNumber(stopsLevel).times(pointStep).plus(ask);
        break;
      case EOrderType.SELL_STOP:
      case EOrderType.SELL_STOP_LIMIT:
        isMinimum = false;
        refPriceBN = new BigNumber(bid).minus(new BigNumber(stopsLevel).times(pointStep));
        break;
    }

    let newPriceBN: BigNumber | undefined;
    const price = this.formGetValues?.('price');

    if (price && refPriceBN) {
      const nextValueBN = isIncrement ? new BigNumber(price).plus(pointStep) : new BigNumber(price).minus(pointStep);

      if ((isMinimum && nextValueBN.gt(refPriceBN)) || (!isMinimum && nextValueBN.lt(refPriceBN))) {
        newPriceBN = nextValueBN;
      } else {
        newPriceBN = refPriceBN;
      }
    } else {
      newPriceBN = refPriceBN;
    }
    this.formSetValue?.('price', newPriceBN?.toFixed(decimal), { shouldValidate: true, shouldDirty: true });
    this.formRecalculateSLTP?.();
  };

  public handlePriceIncrease = () => this.handlePriceAdjust(true);
  public handlePriceDecrease = () => this.handlePriceAdjust(false);

  public handlePriceTriggerChange = (value?: string) => {
    this.formSetValue?.('priceTrigger', value, { shouldValidate: true, shouldDirty: true });
  };

  public handlePriceTriggerBlur = () => {
    const { decimal = 0 } = this.context.symbolDetails || {};
    const priceTrigger = this.formGetValues?.('priceTrigger');
    let priceTriggerBN = priceTrigger ? new BigNumber(priceTrigger) : undefined;
    if (priceTriggerBN?.isNegative()) {
      priceTriggerBN = undefined;
    }
    this.formSetValue?.('priceTrigger', priceTriggerBN?.toFixed(decimal), {
      shouldValidate: true,
      shouldDirty: true
    });
    if (priceTriggerBN) {
      this.formRecalculateSLTP?.();
    }
  };

  public handlePriceTriggerAdjust = (isIncrement?: boolean) => {
    const { buySellType, symbolDetails } = this.context;
    const price = this.formGetValues?.('price');

    if (!symbolDetails || !price) {
      this.formSetValue?.('priceTrigger', undefined, { shouldValidate: true, shouldDirty: true });
      this.formRecalculateSLTP?.();
      return;
    }

    const { pointStep, stopsLevel, decimal } = destructureAsNumber(symbolDetails, [
      'pointStep',
      'stopsLevel',
      'decimal'
    ]);
    let refPriceBN;
    let isMinimum;
    if (buySellType === EBuySellType.BUY) {
      isMinimum = false;
      refPriceBN = new BigNumber(price).minus(new BigNumber(stopsLevel).times(pointStep));
    } else {
      isMinimum = true;
      refPriceBN = new BigNumber(stopsLevel).times(pointStep).plus(price);
    }

    let newPriceBN: BigNumber | undefined;
    const priceTrigger = this.formGetValues?.('priceTrigger');

    if (priceTrigger && refPriceBN) {
      const nextValueBN = isIncrement
        ? new BigNumber(priceTrigger).plus(pointStep)
        : new BigNumber(priceTrigger).minus(pointStep);

      if ((isMinimum && nextValueBN.gt(refPriceBN)) || (!isMinimum && nextValueBN.lt(refPriceBN))) {
        newPriceBN = nextValueBN;
      } else {
        newPriceBN = refPriceBN;
      }
    } else {
      newPriceBN = refPriceBN;
    }
    this.formSetValue?.('priceTrigger', newPriceBN?.toFixed(decimal), { shouldValidate: true, shouldDirty: true });
    this.formRecalculateSLTP?.();
  };

  public handlePriceTriggerIncrease = () => this.handlePriceTriggerAdjust(true);
  public handlePriceTriggerDecrease = () => this.handlePriceTriggerAdjust(false);

  public getPriceDesc = (forPriceTrigger?: boolean) => {
    const { orderType, symbolDetails } = this.context;
    const stopsLevel = numberOrDefault(symbolDetails?.stopsLevel);
    if (forPriceTrigger) {
      if (orderType === EOrderType.BUY_STOP_LIMIT) {
        return i18n.t('Min. {{points}} points below Price', { points: stopsLevel });
      } else if (orderType === EOrderType.SELL_STOP_LIMIT) {
        return i18n.t('Min. {{points}} points above Price', { points: stopsLevel });
      }
    } else {
      switch (orderType) {
        case EOrderType.BUY_LIMIT:
          return i18n.t('Min. {{points}} points below Buy price', { points: stopsLevel });
        case EOrderType.SELL_LIMIT:
          return i18n.t('Min. {{points}} points above Sell price', { points: stopsLevel });
        case EOrderType.BUY_STOP:
        case EOrderType.BUY_STOP_LIMIT:
          return i18n.t('Min. {{points}} points above Buy price', { points: stopsLevel });
        case EOrderType.SELL_STOP:
        case EOrderType.SELL_STOP_LIMIT:
          return i18n.t('Min. {{points}} points below Sell price', { points: stopsLevel });
      }
    }
    return undefined;
  };

  public handleStopLossPriceChange = (existingValue?: string, changeType = EPricePointsChangeType.VALUE) => {
    const { buySellType, symbolDetails } = this.context;
    const { decimal, pointStep, stopsLevel } = destructureAsNumber(symbolDetails, [
      'decimal',
      'pointStep',
      'stopsLevel'
    ]);

    const refValue = this.getCalculatePricePointsRefValue();
    if (!refValue) {
      return undefined;
    }

    const refPrice =
      buySellType === EBuySellType.BUY
        ? // Sell Price - (Min. Point Distance * One Point Equals)
          new BigNumber(refValue).minus(new BigNumber(stopsLevel).times(pointStep))
        : // Buy Price + (Min. Point Distance * One Point Equals)
          new BigNumber(stopsLevel).times(pointStep).plus(refValue);
    if (existingValue) {
      let newValue: BigNumber;
      switch (changeType) {
        case EPricePointsChangeType.VALUE:
          return new BigNumber(existingValue).toFixed(decimal);
        case EPricePointsChangeType.INCREMENT:
          newValue = new BigNumber(existingValue).plus(pointStep);
          break;
        case EPricePointsChangeType.DECREMENT:
          newValue = new BigNumber(existingValue).minus(pointStep);
          break;
      }
      if (
        (buySellType === EBuySellType.BUY && newValue.lt(refPrice)) ||
        (buySellType === EBuySellType.SELL && newValue.gt(refPrice))
      ) {
        return newValue.toFixed(decimal);
      }
    } else if (changeType === EPricePointsChangeType.VALUE) {
      return undefined;
    }

    return refPrice.toFixed(decimal);
  };

  public handleTakeProfitPriceChange = (existingValue?: string, changeType = EPricePointsChangeType.VALUE) => {
    const { buySellType, symbolDetails } = this.context;
    const { decimal, pointStep, stopsLevel } = destructureAsNumber(symbolDetails, [
      'decimal',
      'pointStep',
      'stopsLevel'
    ]);

    const refValue = this.getCalculatePricePointsRefValue();
    if (!refValue) {
      return undefined;
    }

    const refPrice =
      buySellType === EBuySellType.BUY
        ? // Sell Price + (Min. Point Distance * One Point Equals)
          new BigNumber(stopsLevel).times(pointStep).plus(refValue)
        : // Buy Price - (Min. Point Distance * One Point Equals
          new BigNumber(refValue).minus(new BigNumber(stopsLevel).times(pointStep));
    if (existingValue) {
      let newValue: BigNumber;
      switch (changeType) {
        case EPricePointsChangeType.VALUE:
          return new BigNumber(existingValue).toFixed(decimal);
        case EPricePointsChangeType.INCREMENT:
          newValue = new BigNumber(existingValue).plus(pointStep);
          break;
        case EPricePointsChangeType.DECREMENT:
          newValue = new BigNumber(existingValue).minus(pointStep);
          break;
      }
      if (
        (buySellType === EBuySellType.BUY && newValue.gt(refPrice)) ||
        (buySellType === EBuySellType.SELL && newValue.lt(refPrice))
      ) {
        return newValue.toFixed(decimal);
      }
    } else if (changeType === EPricePointsChangeType.VALUE) {
      return undefined;
    }

    return refPrice.toFixed(decimal);
  };

  public handlePointsChange = (existingValue?: string, changeType = EPricePointsChangeType.VALUE) => {
    const stopsLevel = numberOrDefault(this.context.symbolDetails?.stopsLevel);
    const minPoints = new BigNumber(stopsLevel);

    if (existingValue) {
      let newValue: BigNumber;
      switch (changeType) {
        case EPricePointsChangeType.VALUE:
          return new BigNumber(existingValue).toFixed(0);
        case EPricePointsChangeType.INCREMENT:
          newValue = new BigNumber(existingValue).plus(1);
          break;
        case EPricePointsChangeType.DECREMENT:
          newValue = new BigNumber(existingValue).minus(1);
          break;
      }
      if (newValue.gt(minPoints)) {
        return newValue.toFixed(0);
      }
    } else if (changeType === EPricePointsChangeType.VALUE) {
      return undefined;
    }

    return minPoints.toFixed(0);
  };

  public getCalculatePricePointsRefValue = (forDescription?: boolean) => {
    const { buySellType, orderTypeLabel, symbolDetails } = this.context;
    const { ask, bid } = destructureAsNumber(symbolDetails, ['ask', 'bid']);
    switch (orderTypeLabel) {
      case EOrderTypeLabel.MARKET:
        if (buySellType === EBuySellType.BUY) {
          // for description, Buy Price; for calculation, Sell Price
          return new BigNumber(forDescription ? ask : bid);
        } else if (buySellType === EBuySellType.SELL) {
          // for description, Sell Price; for calculation, Buy Price
          return new BigNumber(forDescription ? bid : ask);
        }
        break;
      case EOrderTypeLabel.LIMIT:
      // Price (L)
      case EOrderTypeLabel.STOP:
        // Price (S)
        const price = this.formGetValues?.('price');
        return price ? new BigNumber(price) : undefined;
      case EOrderTypeLabel.STOP_LIMIT:
        // Stop Limit Price
        const stopLimitPrice = this.formGetValues?.('priceTrigger');
        return stopLimitPrice ? new BigNumber(stopLimitPrice) : undefined;
    }
    return undefined;
  };

  public getPricePointsDesc = ({
    volume,
    price,
    excludeCurrency
  }: IGetPricePointsDescParams): [string, boolean] | undefined => {
    if (!volume || !price) {
      return undefined;
    }
    const { symbolDetails, buySellType } = this.context;
    const { contractSize, marginCurrency = '' } = symbolDetails || {};
    const contractSizeSafe = numberOrDefault(contractSize);
    const refValue = this.getCalculatePricePointsRefValue(true);
    if (!refValue) {
      return undefined;
    }
    let calculatedValue;
    if (buySellType === EBuySellType.BUY) {
      calculatedValue = new BigNumber(price).minus(refValue).times(volume).times(contractSizeSafe);
    } else if (buySellType === EBuySellType.SELL) {
      calculatedValue = new BigNumber(refValue).minus(price).times(volume).times(contractSizeSafe);
    }
    return calculatedValue
      ? [`${calculatedValue.toFormat(2)}${excludeCurrency ? '' : ` ${marginCurrency}`}`, calculatedValue.isNegative()]
      : undefined;
  };

  public formatSLTPPrice = (price?: string) => {
    const newPrice = price ? new BigNumber(price) : undefined;

    if (!newPrice) {
      return undefined;
    }

    const { decimal } = destructureAsNumber(this.context.symbolDetails, ['decimal']);
    return newPrice.toFixed(decimal);
  };

  public calculateStopLossPricePoints = (isPrice?: boolean, value?: string) => {
    const refValue = this.getCalculatePricePointsRefValue();

    if (!value || !refValue) {
      return undefined;
    }

    const { buySellType, symbolDetails } = this.context;
    const { decimal, pointStep } = destructureAsNumber(symbolDetails, ['decimal', 'pointStep']);
    const valueBN = new BigNumber(value);

    if (valueBN.eq(0)) {
      return valueBN.toFixed(isPrice ? 0 : decimal);
    }

    if (isPrice) {
      if (buySellType === EBuySellType.BUY) {
        // (ref value - price field) / one point equals
        return new BigNumber(refValue).minus(valueBN).div(pointStep).toFixed(0);
      } else if (buySellType === EBuySellType.SELL) {
        // (price field - ref value) / one point equals
        return valueBN.minus(refValue).div(pointStep).toFixed(0);
      }
    } else {
      if (buySellType === EBuySellType.BUY) {
        // ref value - (points field * one point equals)
        return new BigNumber(refValue).minus(new BigNumber(valueBN).times(pointStep)).toFixed(decimal);
      } else if (buySellType === EBuySellType.SELL) {
        // ref value + (points field * one point equals)
        return valueBN.times(pointStep).plus(refValue).toFixed(decimal);
      }
    }
    return undefined;
  };

  public calculateTakeProfitPricePoints = (isPrice?: boolean, value?: string) => {
    const refValue = this.getCalculatePricePointsRefValue();

    if (!value || !refValue) {
      return undefined;
    }

    const { buySellType, symbolDetails } = this.context;
    const { decimal, pointStep } = destructureAsNumber(symbolDetails, ['decimal', 'pointStep']);
    const valueBN = new BigNumber(value);

    if (valueBN.eq(0)) {
      return valueBN.toFixed(isPrice ? 0 : decimal);
    }

    if (isPrice) {
      if (buySellType === EBuySellType.BUY) {
        // (price field - ref value) / one point equals
        return valueBN.minus(refValue).div(pointStep).toFixed(0);
      } else if (buySellType === EBuySellType.SELL) {
        // (ref value - price field) / one point equals
        return new BigNumber(refValue).minus(valueBN).div(pointStep).toFixed(0);
      }
    } else {
      if (buySellType === EBuySellType.BUY) {
        // ref value + (points field * one point equals)
        return valueBN.times(pointStep).plus(refValue).toFixed(decimal);
      } else if (buySellType === EBuySellType.SELL) {
        // ref value - (points field * one point equals)
        return new BigNumber(refValue).minus(new BigNumber(valueBN).times(pointStep)).toFixed(decimal);
      }
    }
    return undefined;
  };

  public calculateStopLosInPoints = (params: ICalculateStopLosInPointsParams) => {
    const { orderType, pointStep } = params;
    const { currentPrice, stopLoss } = destructureAsBigNumber(params, ['currentPrice', 'stopLoss']);

    if (stopLoss.eq(0)) {
      return stopLoss.toFixed(0);
    }

    if (orderType === EOrderType.BUY) {
      return currentPrice.minus(stopLoss).div(pointStep).toFixed(0);
    } else if (orderType === EOrderType.SELL) {
      return stopLoss.minus(currentPrice).div(pointStep).toFixed(0);
    }
    return undefined;
  };

  /**
   * Bid and Ask are only for MARGIN_CALC_CFDINDEX
   * */
  // eslint-disable-next-line complexity
  public calculateMarginRequirement = (defaultMarginRequirement = DEFAULT_MARGIN_REQUIREMENT) => {
    // For any value that is 0 or invalid (e.g. NaN)
    const fallbackMarginValue = 'N.A.';

    const volume = this.formGetValues?.('volume');
    const volumeBN = volume ? new BigNumber(volume) : undefined;
    const limitOrderPrice = this.formGetValues?.('price');
    const { orderType, symbolDetails } = this.context;
    const { marginMode, bid, ask } = destructureObject(symbolDetails, ['marginMode', 'bid', 'ask']);
    const { leverage } = destructureObject(authService.context.selectedTradingAccount, ['leverage']);
    const refValue = this.getCalculatePricePointsRefValue(true);

    if (!volumeBN || !refValue || typeof limitOrderPrice !== 'string') {
      return defaultMarginRequirement;
    }

    if (!leverage) {
      return fallbackMarginValue;
    }

    const {
      contractSize,
      marginPercentage,
      exchangeRate,
      marginInitial,
      tickSize
    } = destructureAsBigNumber(symbolDetails, [
      'contractSize',
      'marginPercentage',
      'exchangeRate',
      'marginInitial',
      'tickSize'
    ]);

    let calculatedMargin: BigNumber;
    switch (marginMode) {
      case EMarginMode.MARGIN_CALC_FOREX:
        calculatedMargin = volumeBN
          .times(contractSize)
          .div(leverage)
          .times(marginPercentage.div(100))
          .times(exchangeRate);
        break;

      case EMarginMode.MARGIN_CALC_CFD:
        calculatedMargin = volumeBN
          .times(contractSize)
          .times(refValue)
          .times(marginPercentage.div(100))
          .times(exchangeRate);
        break;

      case EMarginMode.MARGIN_CALC_CFDLEVERAGE:
        calculatedMargin = volumeBN
          .times(contractSize)
          .times(refValue)
          .div(leverage)
          .times(marginPercentage.div(100))
          .times(exchangeRate);
        break;

      case EMarginMode.MARGIN_CALC_FUTURES:
        calculatedMargin = volumeBN.times(marginInitial).times(marginPercentage.div(100)).times(exchangeRate);
        break;

      case EMarginMode.MARGIN_CALC_CFDINDEX:
        if (tickSize.isZero()) {
          return fallbackMarginValue;
        }

        if (isMarketOrder(orderType)) {
          calculatedMargin = volumeBN
            .times(contractSize)
            .times(refValue)
            .div(tickSize)
            .times(refValue)
            .times(marginPercentage.div(100))
            .times(exchangeRate);
          break;
        }
        // For BUY_LIMIT, BUY_STOP, BUY_STOP_LIMIT use ask price
        // For SELL_LIMIT, SELL_STOP, SELL_STOP_LIMIT use bid price
        const marketPrice = isBuyTypeOrder(orderType) ? ask : bid;

        calculatedMargin = volumeBN
          .times(contractSize)
          .times(marketPrice ?? 0)
          .div(tickSize)
          .times(limitOrderPrice)
          .times(marginPercentage.div(100))
          .times(exchangeRate);

        break;

      case EMarginMode.TRADE_MODE_FOREX_NO_LEVERAGE:
        calculatedMargin = volumeBN.times(contractSize).times(marginPercentage.div(100)).times(exchangeRate);
        break;

      default:
        return fallbackMarginValue;
    }
    return calculatedMargin.isFinite() && calculatedMargin.gt(0) ? calculatedMargin.toFormat(2) : fallbackMarginValue;
  };

  public calculateMarginUsed = (params: ICalculateMarginUsedParams) => {
    const { marginMode } = params;
    const { symbolDetails } = this.context;
    const { entryPrice, contractSize, exchangeRate, marginPercentage, volume } = destructureAsBigNumber(params, [
      'entryPrice',
      'contractSize',
      'exchangeRate',
      'marginPercentage',
      'volume'
    ]);
    const { leverage } = destructureObject(authService.context.selectedTradingAccount, ['leverage']);
    const { marginInitial, tickSize } = destructureAsBigNumber(symbolDetails, ['marginInitial', 'tickSize']);
    const refValue = this.getCalculatePricePointsRefValue(true);

    if (!volume || !entryPrice || !refValue) {
      return DEFAULT_MARGIN_USED;
    }

    if (!leverage) {
      return 'N.A.';
    }

    let calculatedMargin: BigNumber;
    switch (marginMode) {
      case EMarginMode.MARGIN_CALC_FOREX:
        calculatedMargin = volume
          .times(contractSize)
          .div(leverage)
          .times(marginPercentage.div(100))
          .times(exchangeRate);
        break;

      case EMarginMode.MARGIN_CALC_CFD:
        calculatedMargin = volume
          .times(contractSize)
          .times(entryPrice)
          .times(marginPercentage.div(100))
          .times(exchangeRate);
        break;

      case EMarginMode.MARGIN_CALC_FUTURES:
        calculatedMargin = volume.times(marginInitial).times(marginPercentage.div(100)).times(exchangeRate);
        break;

      case EMarginMode.MARGIN_CALC_CFDINDEX:
        calculatedMargin = volume
          .times(contractSize)
          .times(refValue.div(tickSize))
          .times(entryPrice)
          .times(marginPercentage.div(100))
          .times(exchangeRate);
        break;

      case EMarginMode.MARGIN_CALC_CFDLEVERAGE:
        calculatedMargin = volume
          .times(contractSize)
          .times(entryPrice)
          .div(leverage)
          .times(marginPercentage.div(100))
          .times(exchangeRate);
        break;

      case EMarginMode.TRADE_MODE_FOREX_NO_LEVERAGE:
        calculatedMargin = volume.times(contractSize).times(marginPercentage.div(100)).times(exchangeRate);
        break;

      default:
        return 'N.A.';
    }
    return calculatedMargin.toFormat(2);
  };

  public calculatePotentialGainOrLoss = (params: ICalculatePotentialGainOrLoss) => {
    const { buySellType, contractSize, currentPrice, exchangeRate, refValue, volume } = params;

    if (buySellType === EBuySellType.BUY) {
      return new BigNumber(refValue)
        .minus(currentPrice)
        .times(volume)
        .times(contractSize)
        .times(exchangeRate)
        .toFormat(2);
    } else {
      return currentPrice.minus(refValue).times(volume).times(contractSize).times(exchangeRate).toFormat(2);
    }
  };

  // eslint-disable-next-line complexity
  public createMarketTrade = async ({ values, mixpanelProperties }: ICreateTradeFunctionParams) => {
    const { symbolDetails, orderType, buySellType } = this.context;
    const { symbol, tradableExeMode, contractSize, lotSizeStep } = symbolDetails || {};
    const { volume, fillPolicy, stopLoss, takeProfit, orderTypeLabel } = values;
    const { displayTime } = mixpanelProperties || {};

    if (!symbol || orderType === undefined || !volume || this.checkIfNotTradable(tradableExeMode)) {
      return undefined;
    }

    const units = convertVolumeToUnits(Number(volume), contractSize, lotSizeStep);

    const params: ICreateMarketTradeParams = {
      orderType: orderType as TMarketTradeOrderType,
      symbol,
      lotSize: Number(volume),
      fillPolicy: fillPolicy,
      indicate: EOrderIndicate.PRICE,
      stopLoss: stopLoss ? Number(stopLoss) : undefined,
      takeProfit: takeProfit ? Number(takeProfit) : undefined,
      units
    };

    try {
      this.context.loading = true;
      await marketTradeAPI.create(params);
      if (symbolDetails && orderTypeLabel && buySellType !== undefined) {
        mixpanelAnalysisService.order.tradeOrderSubmit({
          values,
          symbolDetails,
          buySellType,
          displayTime
        });
      }
      return this.generateOrderPlacedMessage(params);
    } catch (err) {
      throw err as IApiError;
    } finally {
      this.context.loading = false;
    }
  };

  // eslint-disable-next-line complexity
  public createLimitTrade = async ({ values, mixpanelProperties }: ICreateTradeFunctionParams) => {
    const { symbolDetails, orderType, orderTypeLabel, buySellType } = this.context;
    const { symbol, tradableExeMode, contractSize, lotSizeStep } = symbolDetails || {};
    const {
      volume,
      fillPolicy,
      price,
      priceTrigger,
      stopLoss,
      takeProfit,
      tradeExpiry,
      expiration,
      centroidDeviation
    } = values;
    const { displayTime } = mixpanelProperties || {};

    const hasExpiry = tradeExpiry === EOrderExpiry.SPECIFIED_DAY || tradeExpiry === EOrderExpiry.SPECIFIED_TIMESTAMP;
    if (
      !symbol ||
      orderType === undefined ||
      !volume ||
      !price ||
      !tradeExpiry ||
      (hasExpiry && !expiration) ||
      (orderTypeLabel === EOrderTypeLabel.STOP_LIMIT && !priceTrigger) ||
      this.checkIfNotTradable(tradableExeMode)
    ) {
      return undefined;
    }

    // WT-7944 BE Requirement for creating limit orders of tradeExpiry:
    // - SPECIFIED_DAY: Always convert to server time
    // - SPECIFIED_TIMESTAMP: convert to correct UTC time
    const expiryDayjsWithOffset =
      tradeExpiry === EOrderExpiry.SPECIFIED_DAY
        ? companyService.convertToDayjsWithOffsetServerTime({ input: expiration, keepTime: true })
        : companyService.convertToDayjsWithOffset({ input: expiration, keepTime: true });

    const convertedExpiration = getSecondsSinceEpoch(expiryDayjsWithOffset);

    const expirationParams =
      tradeExpiry === EOrderExpiry.SPECIFIED_DAY || tradeExpiry === EOrderExpiry.SPECIFIED_TIMESTAMP
        ? convertedExpiration
        : undefined;

    const units = convertVolumeToUnits(Number(volume), contractSize, lotSizeStep);

    const params: ICreateLimitTradeParams = {
      orderType: orderType as TLimitTradeOrderType,
      symbol,
      lotSize: Number(volume),
      fillPolicy,
      price: Number(price),
      priceTrigger: priceTrigger ? Number(priceTrigger) : undefined,
      indicate: EOrderIndicate.PRICE,
      tradeExpiry,
      expiration: expirationParams,
      stopLoss: stopLoss ? Number(stopLoss) : undefined,
      takeProfit: takeProfit ? Number(takeProfit) : undefined,
      centroidDeviation: centroidDeviation ? Number(centroidDeviation) : undefined,
      units
    };

    try {
      this.context.loading = true;
      await limitTradeAPI.create(params);
      if (orderTypeLabel && symbolDetails && buySellType !== undefined) {
        mixpanelAnalysisService.order.tradeOrderSubmit({
          values,
          symbolDetails,
          buySellType,
          displayTime
        });
      }
      return this.generateOrderPlacedMessage(params);
    } catch (err) {
      throw err as IApiError;
    } finally {
      this.context.loading = false;
    }
  };

  public updateMarketOrder = async ({ values, mixpanelProperties }: IUpdateOrderParams) => {
    const { orderId, symbol, lotSize, openPrice, decimal, contractSize, lotSizeStep, fillPolicy } =
      this.context.order || {};
    const { stopLoss, takeProfit } = values;
    const { livePrice } = mixpanelProperties;

    if (!orderId || !symbol) {
      return undefined;
    }

    const units = convertVolumeToUnits(lotSize, contractSize, lotSizeStep);

    const params: IUpdateMarketTradeParams = {
      orderId,
      symbol,
      indicate: EOrderIndicate.PRICE,
      stopLoss: stopLoss ? Number(stopLoss) : undefined,
      takeProfit: takeProfit ? Number(takeProfit) : undefined,
      units,
      fillPolicy
    };

    if (this.context.order) {
      mixpanelAnalysisService.order.editOrderSubmitted({
        livePrice,
        order: this.context.order,
        values,
        positionModuleTab: EMixpanelPositionModuleTab.OPEN_POSITIONS
      });
    }

    try {
      this.context.loading = true;
      await marketTradeAPI.update(params);
      orderService.resetOpenOrdersInitialCall();
      // TODO: Remove this condition checking after changing Edit Order implementation
      if (configService.getAppType() === EAppType.PC) {
        // !!! Make sure stopLoss and takeProfit is always price instead of points
        chartService.updateTrackOrderPrice({
          orderId: Number(orderId),
          decimal: decimal ?? 2,
          stopLoss: params.stopLoss,
          takeProfit: params.takeProfit
        });
      }
      return this.generateOrderPlacedMessage({
        ...params,
        lotSize,
        price: openPrice,
        orderId: Number(orderId)
      });
    } catch (err) {
      throw err as IApiError;
    } finally {
      this.context.loading = false;
    }
  };

  private getUpdateLimitOrderExpirationTime = (tradeExpiry: EOrderExpiry, expiration: number | undefined) => {
    const isSpecifiedDay = tradeExpiry === EOrderExpiry.SPECIFIED_DAY;
    const isSpecifiedTimestamp = tradeExpiry === EOrderExpiry.SPECIFIED_TIMESTAMP;
    const isUpdateDate = isSpecifiedDay || isSpecifiedTimestamp;
    if (!expiration || !isUpdateDate) {
      return undefined;
    }
    if (isSpecifiedDay) {
      const isServerTime = companyService.getMarketTimeIsServer();

      // WT-7944 BE Requirement for updating limit orders of tradeExpiry for specified day:
      // We must submit the expiration time in server time
      // Mobile + Server time: need convert to sever time. When we click on the calender, it takes device time start of day
      // Web + Server time: need convert to sever time. When we click on the calender, it takes device time start of day
      // Mobile + device time: no need convert to server time, calender does +1/-1 day and gives server time start of day
      // Web + device time : need convert to sever time. When we click on the calender, it takes device time start of day
      if (isServerTime) {
        return getSecondsSinceEpoch(
          companyService.convertToDayjsWithOffsetServerTime({ input: expiration, keepTime: true })
        );
      } else {
        return configService.getAppType() === EAppType.MOBILE
          ? expiration
          : getSecondsSinceEpoch(
              companyService.convertToDayjsWithOffsetServerTime({ input: expiration, keepTime: true })
            );
      }
    }

    // For specified timestamp: convert to correct UTC time
    return getSecondsSinceEpoch(companyService.convertToDayjsWithOffset({ input: expiration, keepTime: true }));
  };

  // eslint-disable-next-line complexity
  public updateLimitOrder = async ({ values, mixpanelProperties }: IUpdateOrderParams) => {
    const { loading, orderTypeLabel, pendingOrder, symbolDetails } = this.context;
    const { contractSize } = symbolDetails || {};
    const { orderType, orderId, symbol, lotSize, decimal, lotSizeStep } = pendingOrder || {};
    const { price, priceTrigger, stopLoss, takeProfit, tradeExpiry, expiration } = values;
    const { livePrice } = mixpanelProperties || {};

    if (
      loading ||
      !orderId ||
      !symbol ||
      !price ||
      (orderTypeLabel === EOrderTypeLabel.STOP_LIMIT && !priceTrigger) ||
      tradeExpiry === undefined ||
      !(tradeExpiry in EOrderExpiry)
    ) {
      return undefined;
    }

    const tradeExpiration = this.getUpdateLimitOrderExpirationTime(tradeExpiry, expiration);

    const units = convertVolumeToUnits(lotSize, contractSize, lotSizeStep);

    const params: IUpdateLimitTradeParams = {
      orderId,
      symbol,
      price: Number(price),
      priceTrigger: priceTrigger ? Number(priceTrigger) : undefined,
      indicate: EOrderIndicate.PRICE,
      stopLoss: stopLoss ? Number(stopLoss) : undefined,
      takeProfit: takeProfit ? Number(takeProfit) : undefined,
      tradeExpiry,
      units,
      expiration: tradeExpiration
    };

    mixpanelAnalysisService.order.editOrderSubmitted({
      livePrice,
      order: pendingOrder as IPendingOrder,
      values,
      positionModuleTab: EMixpanelPositionModuleTab.PENDING_ORDERS
    });

    try {
      this.context.loading = true;
      await limitTradeAPI.update(params);
      orderService.resetPendingOrdersInitialCall();
      // TODO: Remove this condition checking after changing Edit Order implementation
      if (configService.getAppType() === EAppType.PC) {
        // !!! Make sure stopLoss and takeProfit is always price instead of points
        const { entryPrice, additionalPrice } = chartService.getEntryAndAdditionalPrices({
          orderType,
          price: Number(price),
          priceTrigger: priceTrigger ? Number(priceTrigger) : 0
        });
        const entryPrices = [entryPrice];
        if (additionalPrice !== undefined) {
          entryPrices.push(additionalPrice);
        }
        chartService.updateTrackOrderPrice({
          orderId: Number(orderId),
          decimal: decimal ?? 2,
          entryPrices,
          stopLoss: params.stopLoss,
          takeProfit: params.takeProfit
        });
      }
      return this.generateOrderPlacedMessage({
        ...params,
        lotSize,
        orderId: Number(orderId)
      });
    } catch (err) {
      throw err as IApiError;
    } finally {
      this.context.loading = false;
    }
  };

  // eslint-disable-next-line complexity
  protected generateOrderPlacedMessage = (values: IGenerateOrderPlacedMessageParams) => {
    const { orderType, symbolDetails } = this.context;

    if (orderType === undefined || !symbolDetails) {
      return '';
    }

    const snapshotStopLoss = this.estimatedStopLoss;
    const snapshotTakeProfit = this.estimatedTakeProfit;

    const integration = companyService.getIntegration();
    const sizeLabel = integration === EMainProductCodeType.METATRADER5 ? i18n.t('Volume') : i18n.t('Size');
    const { decimal, symbol } = symbolDetails;
    const {
      price,
      priceTrigger,
      lotSize,
      indicate = EOrderIndicate.PRICE,
      stopLoss,
      takeProfit,
      orderId,
      units
    } = values;

    const sellOrBuyText = mapOrderTypeToText(orderType, true);

    let message = orderId
      ? i18n.t('{{symbol}} - {{sellOrBuyText}} updated, {{sizeLabel}}: {{lotSize}} / Units: {{units}}.', {
          symbol,
          sellOrBuyText,
          sizeLabel,
          lotSize: lotSize && formatNumberWithThousandSeparator(lotSize?.toLocaleString()),
          units: units && formatNumberWithThousandSeparator(units)
        })
      : i18n.t('{{symbol}} - {{sellOrBuyText}} placed, {{sizeLabel}}: {{lotSize}} / Units: {{units}}.', {
          symbol,
          sellOrBuyText,
          sizeLabel,
          lotSize: lotSize && formatNumberWithThousandSeparator(lotSize?.toLocaleString()),
          units: units && formatNumberWithThousandSeparator(units)
        });

    if (priceTrigger) {
      message += ` ${i18n.t('Stop Limit Price')}: ${formatDecimal(priceTrigger, decimal)}.`;
    }

    if (price) {
      message += ` ${checkIsOpenPosition(orderType) ? i18n.t('Entry Price') : i18n.t('Price')}: ${formatDecimal(
        price,
        decimal
      )}.`;
    }

    if (stopLoss) {
      switch (indicate) {
        case EOrderIndicate.POINTS:
          message += ` ${i18n.t('Estimated stop loss')}: ${formatDecimal(snapshotStopLoss, decimal)}.`;

          break;
        default:
          message += ` ${i18n.t('Stop Loss')}: ${formatDecimal(stopLoss, decimal)}.`;

          break;
      }
    }

    if (takeProfit) {
      switch (indicate) {
        case EOrderIndicate.POINTS:
          message += ` ${i18n.t('Estimated take profit')}: ${formatDecimal(snapshotTakeProfit, decimal)}.`;

          break;
        default:
          message += ` ${i18n.t('Take Profit')}: ${formatDecimal(takeProfit, decimal)}.`;

          break;
      }
    }

    return message;
  };

  public setOrder = (order: IOrder | IPendingOrder) => {
    const orderType = order.orderType;
    switch (orderType) {
      case EOrderType.BUY:
      case EOrderType.SELL:
        this.context.order = order as IOrder;
        this.context.pendingOrder = undefined;
        break;

      case EOrderType.BUY_LIMIT:
      case EOrderType.SELL_LIMIT:
      case EOrderType.BUY_STOP:
      case EOrderType.SELL_STOP:
      case EOrderType.BUY_STOP_LIMIT:
      case EOrderType.SELL_STOP_LIMIT:
        this.context.order = undefined;
        this.context.pendingOrder = order as IPendingOrder;
        break;

      default:
        this.context.order = undefined;
        this.context.pendingOrder = undefined;
        return;
    }

    this.setOrderType(order.orderType);
  };

  public getOrder = () => {
    if (this.isEdit) {
      switch (this.context.orderType) {
        case EOrderType.BUY:
        case EOrderType.SELL:
          return this.context.order;

        case EOrderType.BUY_LIMIT:
        case EOrderType.SELL_LIMIT:
        case EOrderType.BUY_STOP:
        case EOrderType.SELL_STOP:
        case EOrderType.BUY_STOP_LIMIT:
        case EOrderType.SELL_STOP_LIMIT:
          return this.context.pendingOrder;
      }
    }
    return undefined;
  };

  public setShowOrderFormSheet = (show: boolean) => {
    this.context.showOrderFormSheet = show;
  };

  public setOrderType = (orderType?: EOrderType) => {
    switch (orderType) {
      case EOrderType.BUY:
        this.context.buySellType = EBuySellType.BUY;
        this.context.orderTypeLabel = EOrderTypeLabel.MARKET;
        break;
      case EOrderType.BUY_LIMIT:
        this.context.buySellType = EBuySellType.BUY;
        this.context.orderTypeLabel = EOrderTypeLabel.LIMIT;
        break;
      case EOrderType.BUY_STOP:
        this.context.buySellType = EBuySellType.BUY;
        this.context.orderTypeLabel = EOrderTypeLabel.STOP;
        break;
      case EOrderType.BUY_STOP_LIMIT:
        this.context.buySellType = EBuySellType.BUY;
        this.context.orderTypeLabel = EOrderTypeLabel.STOP_LIMIT;
        break;
      case EOrderType.SELL_LIMIT:
        this.context.buySellType = EBuySellType.SELL;
        this.context.orderTypeLabel = EOrderTypeLabel.LIMIT;
        break;
      case EOrderType.SELL_STOP:
        this.context.buySellType = EBuySellType.SELL;
        this.context.orderTypeLabel = EOrderTypeLabel.STOP;
        break;
      case EOrderType.SELL_STOP_LIMIT:
        this.context.buySellType = EBuySellType.SELL;
        this.context.orderTypeLabel = EOrderTypeLabel.STOP_LIMIT;
        break;
      case EOrderType.SELL:
        this.context.buySellType = EBuySellType.SELL;
        this.context.orderTypeLabel = EOrderTypeLabel.MARKET;
        break;
      default:
        this.context.orderType = undefined;
        this.context.buySellType = undefined;
        this.context.orderTypeLabel = undefined;
        this.estimatedStopLoss = undefined;
        this.estimatedTakeProfit = undefined;
        return;
    }
    this.context.orderType = orderType;
    this.estimatedStopLoss = undefined;
    this.estimatedTakeProfit = undefined;
  };

  public refreshOrderType = () => {
    if (this.context.buySellType === EBuySellType.BUY) {
      switch (this.context.orderTypeLabel) {
        case EOrderTypeLabel.MARKET:
          this.context.orderType = EOrderType.BUY;
          return;
        case EOrderTypeLabel.LIMIT:
          this.context.orderType = EOrderType.BUY_LIMIT;
          return;
        case EOrderTypeLabel.STOP:
          this.context.orderType = EOrderType.BUY_STOP;
          return;
        case EOrderTypeLabel.STOP_LIMIT:
          this.context.orderType = EOrderType.BUY_STOP_LIMIT;
          return;
      }
    } else if (this.context.buySellType === EBuySellType.SELL) {
      switch (this.context.orderTypeLabel) {
        case EOrderTypeLabel.MARKET:
          this.context.orderType = EOrderType.SELL;
          return;
        case EOrderTypeLabel.LIMIT:
          this.context.orderType = EOrderType.SELL_LIMIT;
          return;
        case EOrderTypeLabel.STOP:
          this.context.orderType = EOrderType.SELL_STOP;
          return;
        case EOrderTypeLabel.STOP_LIMIT:
          this.context.orderType = EOrderType.SELL_STOP_LIMIT;
          return;
      }
    }
  };

  public setBuySellType = ({ buySellType, mixpanelProperties }: ISetBuySellTypeParams) => {
    if (mixpanelProperties) {
      const { symbol, modalType } = mixpanelProperties || {};
      mixpanelAnalysisService.order.orderFieldClick({
        action:
          buySellType === EBuySellType.BUY
            ? EOrderFieldAction.BUY_BUTTON_CLICKED
            : EOrderFieldAction.SELL_BUTTON_CLICKED,
        symbol,
        modalType
      });
    }
    this.context.buySellType = buySellType;
    this.refreshOrderType();
  };

  public setOrderTypeLabel = (orderTypeLabel?: EOrderTypeLabel) => {
    this.context.orderTypeLabel = orderTypeLabel;
    if (orderTypeLabel) {
      this.formSetValue?.('orderTypeLabel', orderTypeLabel, { shouldValidate: true, shouldDirty: true });
    }
    this.refreshOrderType();
  };

  public getFillPoliciesFromOrderTypeLabel = (orderTypeLabel?: EOrderTypeLabel) => {
    if (this.isEdit) {
      const { fillPolicy } = this.getOrder() || {};
      return fillPolicy ? [fillPolicy] : [];
    } else {
      const { tradeFillPolicy } = this.context.symbolDetails || {};
      const finalTypeLabel = orderTypeLabel ?? this.context.orderTypeLabel;

      if (!tradeFillPolicy) {
        return [];
      }
      let fillPolicies;
      switch (finalTypeLabel) {
        case EOrderTypeLabel.MARKET:
          fillPolicies = tradeFillPolicy.marketFillPolicy;
          break;
        case EOrderTypeLabel.LIMIT:
          fillPolicies = tradeFillPolicy.limitFillPolicy;
          break;
        case EOrderTypeLabel.STOP:
          fillPolicies = tradeFillPolicy.stopFillPolicy;
          break;
        case EOrderTypeLabel.STOP_LIMIT:
          fillPolicies = tradeFillPolicy.stopLimitFillPolicy;
          break;
      }
      return fillPolicies || [];
    }
  };

  public checkIfNotTradable = (tradableExeMode?: boolean) => {
    return !tradableExeMode;
  };

  private getDefaultVolumeForReset = (symbolDetails?: ISymbolDetail, volume?: string) => {
    const { minContractLotSize, lotSizeStep } = symbolDetails || {};
    if (!minContractLotSize || !volume) {
      return '';
    }
    const minContractLotSizeBN = numberAsBigNumberOrUndefined(minContractLotSize);
    const decimals = getDecimalsFromValue(lotSizeStep);
    let defaultVolume = volume ? new BigNumber(volume) : undefined;
    if (defaultVolume && minContractLotSizeBN && minContractLotSizeBN.gt(defaultVolume)) {
      defaultVolume = minContractLotSizeBN;
    }
    return defaultVolume ? trimTrailingZero(defaultVolume?.toFixed(decimals || 0)) : '';
  };

  // Used Volume to calculate Units
  private getDefaultUnitsForReset = (symbolDetails?: ISymbolDetail, volume?: string) => {
    const { minContractLotSize, contractSize } = symbolDetails || {};
    if (!minContractLotSize || !contractSize || !volume) {
      return '';
    }
    const minUnitsBN = numberAsBigNumberOrUndefined(minContractLotSize)?.times(contractSize);
    let defaultUnits = volume ? new BigNumber(volume).times(contractSize) : undefined;
    if (defaultUnits && minUnitsBN && minUnitsBN.gt(defaultUnits)) {
      defaultUnits = minUnitsBN;
    }
    return defaultUnits ? trimTrailingZero(defaultUnits?.toFixed(minUnitsBN?.decimalPlaces() || 0)) : '';
  };

  // eslint-disable-next-line complexity
  private resetFormData = () => {
    const { orderTypeLabel, symbolDetails, resetFormDataParams, savedVolume } = this.context;
    const { integration, override, retainVolume, callback } = resetFormDataParams || {};
    this.context.resetFormDataParams = undefined;
    const { tradableExeMode } = symbolDetails || {};

    const inIntegration = integration ?? companyService.getIntegration();

    const shouldPrefilledFillPolicy =
      inIntegration === EMainProductCodeType.METATRADER5 || inIntegration === EMainProductCodeType.CENTROID;

    const defaultValues: IOrderFormData = {
      fillPolicy: undefined,
      indicate: EOrderIndicate.PRICE,
      orderTypeLabel: orderTypeLabel ?? EOrderTypeLabel.MARKET,
      price: '',
      priceTrigger: '',
      stopLoss: '',
      takeProfit: '',
      tradeExpiry: undefined,
      expiration: 0,
      volume: '',
      units: '',
      centroidDeviation: ''
    };

    if (shouldPrefilledFillPolicy) {
      const availFillPolicies = this.getFillPoliciesFromOrderTypeLabel();
      if (availFillPolicies.length) {
        defaultValues.fillPolicy = availFillPolicies[0];
      }
    }

    if (orderTypeLabel !== EOrderTypeLabel.MARKET) {
      defaultValues.tradeExpiry = EOrderExpiry.GTC;
    }

    // Volume will always be retained for create form
    if (retainVolume || !this.isEdit) {
      defaultValues.volume = this.getDefaultVolumeForReset(symbolDetails, savedVolume);
      defaultValues.units = this.getDefaultUnitsForReset(symbolDetails, savedVolume);
      this.context.savedVolume = defaultValues.volume;
      this.context.savedUnits = defaultValues.units;
    }

    if (!tradableExeMode && orderTypeLabel === EOrderTypeLabel.MARKET) {
      defaultValues.volume = '';
      defaultValues.units = '';
      defaultValues.deviation = 5;
    }

    const finalValues = {
      ...defaultValues,
      ...override
    };

    this.formReset?.(finalValues);
    this.formResetSLTP?.();
    // Add a slight delay to prevent same setState function being replaced from previous two reset function
    setTimeout(() => callback?.(), 1);
  };

  private debouncedResetFormData = debounce(this.resetFormData, 50);

  public doResetFormData = (params?: IResetFormDataParams) => {
    this.context.resetFormDataParams = {
      ...this.context.resetFormDataParams,
      ...params
    };
    this.debouncedResetFormData();
  };
}

export default OrderFormService;
